import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import { ISelfDescribable } from '../viewModels/mobileData.viewModel';
import { WorkConstants } from '../works/works.model';
// import { ObjectInfo } from '@ABC/Graphics/Adapters/OpenLayers/src/models/map.service.model';
export class ComponentDataSource<TColumns extends ISelfDescribable> {
  private _filters: {};
  private _dataSource: MatTableDataSource<TColumns>;
  private _externalFilterCondition: (obj: TColumns) => boolean;
  get filters(): ReadonlyArray<DataSourceFilter> {
    return Object.values(this._filters);
  }
  get visibleFilters(): ReadonlyArray<DataSourceFilter> {
    return this.filters.filter((f) => f.visible);
  }
  get regularFilters(): ReadonlyArray<DataSourceFilter> {
    return this.filters.filter((f) => f.type === FilterType.Regular);
  }
  get predicateFilters(): ReadonlyArray<DataSourceFilter> {
    return this.filters.filter((f) => f.type === FilterType.Predicate);
  }
  get filteredData(): TColumns[] {
    return this._dataSource.filteredData;
  }
  get data(): TColumns[] {
    if (this.externalFilterCondition) {
      let _data = {};
      this.filteredData.forEach((fd, originalIndex) => {
        if (this.externalFilterCondition(fd)) {
          _data[originalIndex] = fd;
        }
      });
      return <TColumns[]>_data;
    }
    return this.filteredData;
  }

  get externalFilterCondition(): (obj: TColumns) => boolean {
    return this._externalFilterCondition;
  }
  set externalFilterCondition(obj) {
    this._externalFilterCondition = obj;
  }
  get rawData(): TColumns[] {
    return this._dataSource.data;
  }
  set rawData(value: TColumns[]) {
    this._dataSource.data = value;
  }
  get sort(): MatSort {
    return this._dataSource.sort;
  }
  set sort(value: MatSort) {
    this._dataSource.sort = value;
  }
  get paginator(): MatPaginator {
    return this._dataSource.paginator;
  }
  set paginator(value: MatPaginator) {
    this._dataSource.paginator = value;
  }
  get isEmpty(): boolean {
    return !this._dataSource.data || this._dataSource.data.length === 0;
  }

  //for iterations, etc.
  get internalDataSource(): MatTableDataSource<TColumns> {
    return this._dataSource;
  }

  constructor() {
    this._filters = {};
    this._dataSource = new MatTableDataSource(null);

    this._dataSource.filterPredicate = (data, _) => {
      const matchingFilters: Set<DataSourceFilter> =
        new Set<DataSourceFilter>();

      for (const filter of this.regularFilters) {
        if (filter.value) {
          const objValues = data.getValues();
          for (let i = 0; i < objValues.length; i++) {
            if (matchingFilters.size !== this.regularFilters.length) {
              const objValue = objValues[i].trim().toLowerCase();
              const appliedFilter = filter.value.toLowerCase();
              if (
                !matchingFilters.has(filter) &&
                objValue.indexOf(appliedFilter) != -1
              ) {
                matchingFilters.add(filter);
              }
            } else {
              i = objValues.length;
            }
          }
        } else if (filter.objectInfos && !matchingFilters.has(filter)) {
          const filterableObject: IFilterable = data as unknown as IFilterable;
          if (
            filterableObject.filterableObjectId ||
            (<any>filterableObject).id
          ) {
            const entities = filter.objectInfos.filter(
              (_) =>
                (filterableObject?.filterableObjectId &&
                  filterableObject.filterableObjectId === _.id) ||
                (!filterableObject?.filterableObjectId &&
                  (<any>filterableObject).id === _.id),
            );
            if (entities?.length) {
              const dispText = this.getFilterDisplayText(filterableObject);
              filter.displayText = dispText;
              matchingFilters.add(filter);
            }
          }
        }
      }

      let objectInResultSet: boolean = true;
      if (this.regularFilters.length > 0) {
        const orOperator: boolean = this.regularFilters.every(
          (f) => f.operator === FilterOperator.Or,
        );
        if (orOperator) {
          objectInResultSet = matchingFilters.size > 0;
        } else {
          objectInResultSet =
            matchingFilters.size === this.regularFilters.length;
        }
      }

      if (objectInResultSet) {
        matchingFilters.clear();
        let predicatFiltersToUse: ReadonlyArray<DataSourceFilter> =
          this.predicateFilters.filter((f) => f.isGlobalPredicate);
        if (!predicatFiltersToUse || predicatFiltersToUse.length === 0) {
          predicatFiltersToUse = this.predicateFilters;
        }
        for (const filter of predicatFiltersToUse) {
          if (filter.enabled) {
            if (filter.predicate(data)) {
              matchingFilters.add(filter);
            }
          }
        }
        objectInResultSet = matchingFilters.size > 0;
      }

      return objectInResultSet;
    };
  }

  private getFilterDisplayText(filterableObject: IFilterable) {
    let ret: string = '';
    if (filterableObject) {
      if (
        filterableObject.objectObjectType &&
        filterableObject.objectDesignation
      )
        ret =
          filterableObject.objectObjectType +
          ' ' +
          filterableObject.objectDesignation;
      else {
        const ticket = (filterableObject as any).mtTicket;
        if (ticket) {
          ret = ticket.Nummer;
        }
      }
    }

    return ret;
  }

  addFilter(filter: DataSourceFilter) {
    if (!this._filters[filter.key]) {
      this._filters[filter.key] = filter;
    }
    this.updateFilter();
  }

  removeFilter(filter: DataSourceFilter) {
    if (filter) {
      if (this._filters[filter.key]) {
        delete this._filters[filter.key];
        this.updateFilter();
      }
    }
  }
  resetFilter() {
    for (const f of this.filters) {
      if (
        f.type === FilterType.Predicate &&
        f.value != WorkConstants.FlagFilter_ImmediateMeasure
      ) {
        f.enabled = true;
      } else {
        delete this._filters[f.key];
      }
    }
    this.updateFilter();
  }

  updateFilter() {
    if (this._dataSource.filteredData) {
      if (this.filters.length === 0) {
        this._dataSource.filter = null;
      } else {
        //value is useless; just trigger the filter
        this._dataSource.filter = this.filters.length + '';
      }
    }
  }
}

export interface IFilterable {
  filterableObjectId: string;
  filterableObjectType: number;
  filterableDisplayText: string;
  objectDesignation: string;
  objectObjectType: string;
}

export enum FilterOperator {
  And = 1,
  Or,
}

export enum FilterType {
  Regular = 1,
  Action,
  Predicate,
}

export class DataSourceFilter {
  private _displayText: string;

  value: string;
  objectInfos: DataEntity[];
  operator: FilterOperator;
  type: FilterType;
  action: any;
  predicate: (obj: any) => boolean;
  enabled: boolean;
  visible: boolean;
  isGlobalPredicate: boolean;

  get key(): string {
    if (this.type === FilterType.Regular) {
      if (this.value) {
        return this.value;
      } else if (this.objectInfos) {
        return this.objectInfos.map((_) => _.objectType + '_' + _.id).join(',');
      }
    } else {
      return this.displayText;
    }
    return '';
  }
  get displayText(): string {
    if (this._displayText) {
      return this._displayText;
    } else if (this.value) {
      return this.value;
    }
    return '';
  }
  set displayText(value: string) {
    this._displayText = value;
  }

  constructor() {
    this.type = FilterType.Regular;
    this.enabled = true;
    this.visible = true;
  }
}
